import React, { useState, useEffect } from 'react';
import { Button, Table, Spinner } from 'react-bootstrap';
import { 
  Users, 
  Search, 
  Mail, 
  Calendar, 
  MoreVertical,
  UserCheck,
  UserX,
  Clock,
  RefreshCw,
  User,
  Building2
} from 'lucide-react';
import { userManageService } from '../../services';
import { useAuth } from '../../context';
import { PERMISSIONS } from '../../constants';
import ManageUserDrawer from './ManageUserDrawer';
import './UsersList.css';

const UsersList = () => {
  const { hasPermission } = useAuth();

  // ✅ Check if user can perform any action
  const canAssignRole = hasPermission(PERMISSIONS.USERS.ROLE_ASSIGN);
  const canSuspend = hasPermission(PERMISSIONS.USERS.SUSPEND);
  const canDelete = hasPermission(PERMISSIONS.USERS.DELETE);

  // ✅ Show actions menu only if user can do at least one thing
  const canManageUsers = canAssignRole || canSuspend || canDelete;

  const [users, setUsers] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    suspended: 0
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    const result = await userManageService.getUsers();
    if (result.success) {
      setUsers(result.data || []);
      setCompanyName(result.company?.name || '');
      const active = (result.data || []).filter(u => u.is_active && !u.is_suspended && !u.deleted_at).length;
      const suspended = (result.data || []).filter(u => u.is_suspended && !u.deleted_at).length;
      setStats({
        total: result.total || (result.data || []).length,
        active: active,
        suspended: suspended
      });
    } else {
      setError(result.message || 'Failed to load users');
    }
    setLoading(false);
  };

  const handleManageUser = (user) => {
    setSelectedUser(user);
    setShowDrawer(true);
    setOpenMenuId(null);
  };

  const handleSaveRole = async (userId, newRoleName) => {
    setUsers(prev => 
      prev.map(u => 
        u.uuid === userId ? { ...u, role: { ...u.role, name: newRoleName } } : u
      )
    );
    return Promise.resolve();
  };

  const handleSuspendUser = async (userId) => {
    setUsers(prev => 
      prev.map(u => 
        u.uuid === userId ? { ...u, is_suspended: true } : u
      )
    );
    setStats(prev => ({
      ...prev,
      active: prev.active - 1,
      suspended: prev.suspended + 1
    }));
    return Promise.resolve();
  };

  const handleReactivateUser = async (userId) => {
    setUsers(prev => 
      prev.map(u => 
        u.uuid === userId ? { ...u, is_suspended: false } : u
      )
    );
    setStats(prev => ({
      ...prev,
      active: prev.active + 1,
      suspended: prev.suspended - 1
    }));
    return Promise.resolve();
  };

  const handleDeactivateUser = async (userId) => {
    setUsers(prev => prev.filter(u => u.uuid !== userId));
    setStats(prev => ({
      ...prev,
      total: prev.total - 1,
      active: prev.active - 1
    }));
    return Promise.resolve();
  };

  const filteredUsers = users.filter(user =>
    (user.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.username || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusConfig = (user) => {
    if (user.deleted_at) {
      return { color: '#EF4444', bg: '#FEE2E2', label: 'Deactivated', icon: UserX };
    }
    if (user.is_suspended) {
      return { color: '#F59E0B', bg: '#FEF3C7', label: 'Suspended', icon: Clock };
    }
    if (user.is_active) {
      return { color: '#10B981', bg: '#D1FAE5', label: 'Active', icon: UserCheck };
    }
    return { color: '#94A3B8', bg: '#F1F5F9', label: 'Pending', icon: Clock };
  };

  const getRoleBadge = (roleName) => {
    const styles = {
      super_admin: { background: '#EDE9FE', color: '#4C1D95' },
      company_admin: { background: '#DBEAFE', color: '#1E3A8A' },
      club_admin: { background: '#DBEAFE', color: '#1E3A8A' },
      club_staff: { background: '#E0F2FE', color: '#0369A1' },
      club_viewer: { background: '#F1F5F9', color: '#64748B' }
    };
    return styles[roleName?.toLowerCase()] || styles.club_viewer;
  };

  return (
    <div className="users-list-page">
      <div className="users-list-container">
        {/* Header */}
        <div className="users-header">
          <div className="users-header-left">
            <div className="users-header-icon">
              <Users size={24} color="white" />
            </div>
            <div>
              <h1 className="users-title">Users</h1>
              <p className="users-subtitle">
                {companyName ? `Manage users in ${companyName}` : 'Manage users in your organization'}
              </p>
            </div>
          </div>
          <button onClick={loadUsers} className="users-refresh-button">
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            Refresh
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="users-error-alert">
            <span>{error}</span>
            <button onClick={() => setError('')}>×</button>
          </div>
        )}

        {/* Stats */}
        <div className="users-stats-grid">
          {[
            { label: 'Total Users', value: stats.total, icon: Users, color: '#0ea5e9', bg: '#E0F2FE' },
            { label: 'Active', value: stats.active, icon: UserCheck, color: '#10B981', bg: '#D1FAE5' },
            { label: 'Suspended', value: stats.suspended, icon: UserX, color: '#EF4444', bg: '#FEE2E2' }
          ].map((stat, index) => (
            <div key={index} className="users-stat-card">
              <div>
                <div className="users-stat-label">{stat.label}</div>
                <div className="users-stat-value">{stat.value}</div>
              </div>
              <div className="users-stat-icon" style={{ background: stat.bg, color: stat.color }}>
                <stat.icon size={20} />
              </div>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="users-search-container">
          <div className="users-search-wrapper">
            <Search size={18} className="users-search-icon" />
            <input
              type="text"
              placeholder="Search users by name, email, or username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="users-search-input"
            />
          </div>
          <div className="users-count">
            <Users size={14} />
            {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} found
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="users-loading-state">
            <Spinner animation="border" className="users-loading-spinner" />
            <p>Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="users-empty-state">
            <Users size={48} className="users-empty-icon" />
            <p className="users-empty-title">No users found</p>
            <p className="users-empty-subtitle">
              {searchTerm ? 'Try adjusting your search' : 'No users in the system yet'}
            </p>
          </div>
        ) : (
          <div className="users-table-wrapper">
            <Table hover className="users-table">
              <thead>
                <tr className="users-table-header">
                  <th>User</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Company</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  {/* ✅ Only show Actions column if user can do something */}
                  {canManageUsers && (
                    <th className="users-actions-header">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const statusConfig = getStatusConfig(user);
                  const StatusIcon = statusConfig.icon;
                  const roleStyle = getRoleBadge(user.role?.name);
                  return (
                    <tr key={user.uuid} className="users-table-row">
                      <td>
                        <div className="users-user-cell">
                          <div className="users-user-avatar">
                            {(user.full_name || user.email || 'U').charAt(0).toUpperCase()}
                          </div>
                          <span className="users-user-name">{user.full_name || user.username || '—'}</span>
                        </div>
                      </td>
                      <td>
                        <div className="users-user-email">
                          <Mail size={14} />
                          {user.email}
                        </div>
                      </td>
                      <td>
                        <span className="users-role-badge" style={roleStyle}>
                          {user.role?.name?.replace('_', ' ') || 'Unknown'}
                        </span>
                      </td>
                      <td>
                        <div className="users-user-company">
                          <Building2 size={14} style={{ marginRight: '6px' }} />
                          {companyName || '—'}
                        </div>
                      </td>
                      <td>
                        <span className="users-status-badge" style={{ 
                          background: statusConfig.bg, 
                          color: statusConfig.color 
                        }}>
                          <StatusIcon size={12} />
                          {statusConfig.label}
                        </span>
                      </td>
                      <td>
                        <div className="users-last-login">
                          <Calendar size={12} />
                          {user.last_login ? new Date(user.last_login).toLocaleDateString() : '—'}
                        </div>
                      </td>
                      {/* ✅ Only show Actions cell if user can do something */}
                      {canManageUsers && (
                        <td className="users-actions-cell">
                          <div className="users-actions-wrapper">
                            <button
                              className="users-action-menu-btn"
                              onClick={() => setOpenMenuId(openMenuId === user.uuid ? null : user.uuid)}
                            >
                              <MoreVertical size={18} />
                            </button>
                            {openMenuId === user.uuid && (
                              <div className="users-action-dropdown">
                                <button
                                  className="users-action-item"
                                  onClick={() => handleManageUser(user)}
                                >
                                  <User size={14} />
                                  Manage user
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {/* ✅ Spacer row - dynamic colSpan based on Actions column */}
                <tr className="users-table-spacer">
                  <td colSpan={canManageUsers ? 7 : 6} style={{ height: '60px' }}></td>
                </tr>
              </tbody>
            </Table>
          </div>
        )}
      </div>

      {/* Manage User Drawer */}
      <ManageUserDrawer
        isOpen={showDrawer}
        onClose={() => setShowDrawer(false)}
        user={selectedUser}
        onSaveRole={handleSaveRole}
        onSuspendUser={handleSuspendUser}
        onReactivateUser={handleReactivateUser}
        onDeactivateUser={handleDeactivateUser}
      />
    </div>
  );
};

export default UsersList;